/**
 * Pricing API Types
 * Zod schemas for Strapi DTOs and API contracts
 */

import { z } from '@spcc/core'

/**
 * Localized text schema (en/ar)
 */
const localizedTextObjectSchema = z.object({
  en: z.string(),
  ar: z.string().optional(),
})

export const localizedTextSchema = z
  .union([localizedTextObjectSchema, z.string()])
  .transform((value) => (typeof value === 'string' ? { en: value } : value))

/**
 * Strapi localization schema (for i18n populate response).
 *
 * Kept loose (`passthrough`) because different content types expose different
 * localized fields (name / description / title / addressLine1 / etc.) and we
 * want transformers to read whatever field they need without having to update
 * this schema for every new entity.
 */
export const strapiLocalizationSchema = z.object({
  id: z.number().optional(),
  documentId: z.string().optional(),
  locale: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
}).passthrough()

/**
 * Media DTO schema (Strapi)
 */
const mediaFormatSchema = z.object({
  url: z.string().optional(),
}).passthrough()

export const mediaDtoSchema = z.object({
  url: z.string().optional(),
  formats: z.record(z.string(), mediaFormatSchema).nullable().optional(),
  alternativeText: z.string().nullable().optional(),
}).passthrough()

/**
 * Category DTO schema
 */
export const categoryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: localizedTextSchema,
  locale: z.string().nullable().optional(),
  localizations: z.array(strapiLocalizationSchema).nullable().optional(),
})

/**
 * Certificate DTO schema (PDF asset)
 */
export const certificateDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  file: z.object({
    id: z.number(),
    name: z.string(),
    url: z.string(),
    size: z.number().optional().default(0),
    mime: z.string().optional().default('application/pdf'),
  }).nullable().optional(),
})

/**
 * Variant DTO schema (from Strapi)
 */
export const variantDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  skuCode: z.string().optional(),
  variantCode: z.string().optional(),
  name: localizedTextSchema,
  description: localizedTextSchema.nullable().optional(),
  barcode: z.string().nullable().optional(),
  weight: z.number().nullable().optional(),
  packagingType: z.enum(['bags', 'bulk']).nullable().optional(),
  packaging: z.enum(['bags', 'bulk']).nullable().optional(),
  images: z.array(mediaDtoSchema).optional().default([]),
  certificates: z.array(certificateDtoSchema).optional().default([]),
  isActive: z.boolean().default(true),
  isAvailable: z.boolean().optional().default(true),
  // Price data (resolved by ecommerce service)
  unitPrice: z.number().nullable().optional(),
  minQuantity: z.number().nullable().optional(),
  maxQuantity: z.number().nullable().optional(),
  minOrderQuantity: z.number().nullable().optional(),
  maxOrderQuantity: z.number().nullable().optional(),
  priceItemDocumentId: z.string().nullable().optional(),
  priceType: z.enum(['fixed', 'variable', 'indexed', 'formula']).nullable().optional(),
  // Strapi i18n support (for mergeLocalizations in the transformer)
  locale: z.string().nullable().optional(),
  localizations: z.array(strapiLocalizationSchema).nullable().optional(),
})

/**
 * SKU (Product) DTO schema
 */
export const skuDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string().optional(),
  name: localizedTextSchema,
  description: localizedTextSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  categories: z.array(categoryDtoSchema).default([]),
  variants: z.array(variantDtoSchema).default([]),
  // Strapi i18n support
  locale: z.string().nullable().optional(),
  localizations: z.array(strapiLocalizationSchema).nullable().optional(),
})

/**
 * Tenant summary DTO
 */
export const tenantSummaryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  name: z.string(),
  channel: z.enum(['b2b', 'ecommerce']),
})

/**
 * Customer group summary DTO
 */
export const customerGroupSummaryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
})

/**
 * Price list summary DTO
 */
export const priceListSummaryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  currency: z.enum(['SAR', 'USD', 'EUR']).default('SAR'),
})

/**
 * User summary DTO
 */
export const userSummaryDtoSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  username: z.string(),
})

/**
 * Pricing profile DTO (from /ecommerce/profile)
 */
export const pricingProfileDtoSchema = z.object({
  user: userSummaryDtoSchema,
  tenant: tenantSummaryDtoSchema.nullable(),
  customerGroup: customerGroupSummaryDtoSchema.nullable(),
  priceList: priceListSummaryDtoSchema.nullable(),
})

/**
 * Products list response DTO
 */
export const productsResponseDtoSchema = z.object({
  data: z.array(skuDtoSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }).optional(),
  }).optional(),
})

/**
 * Single product response DTO
 */
export const productResponseDtoSchema = z.object({
  data: skuDtoSchema,
})

/**
 * Profile response DTO
 */
export const profileResponseDtoSchema = z.object({
  data: pricingProfileDtoSchema,
})

// Inferred TypeScript types
export type LocalizedTextDto = z.infer<typeof localizedTextSchema>
export type MediaDto = z.infer<typeof mediaDtoSchema>
export type CategoryDto = z.infer<typeof categoryDtoSchema>
export type VariantDto = z.infer<typeof variantDtoSchema>
export type SkuDto = z.infer<typeof skuDtoSchema>
export type TenantSummaryDto = z.infer<typeof tenantSummaryDtoSchema>
export type CustomerGroupSummaryDto = z.infer<typeof customerGroupSummaryDtoSchema>
export type PriceListSummaryDto = z.infer<typeof priceListSummaryDtoSchema>
export type UserSummaryDto = z.infer<typeof userSummaryDtoSchema>
export type PricingProfileDto = z.infer<typeof pricingProfileDtoSchema>
export type ProductsResponseDto = z.infer<typeof productsResponseDtoSchema>
export type ProductResponseDto = z.infer<typeof productResponseDtoSchema>
export type ProfileResponseDto = z.infer<typeof profileResponseDtoSchema>

/**
 * Validation helpers
 */
export function validateProductsResponse(data: unknown): ProductsResponseDto {
  return productsResponseDtoSchema.parse(data)
}

export function validateProductResponse(data: unknown): ProductResponseDto {
  return productResponseDtoSchema.parse(data)
}

export function validateProfileResponse(data: unknown): ProfileResponseDto {
  return profileResponseDtoSchema.parse(data)
}

/**
 * API Query params
 */
export interface ProductsQueryParams {
  categoryCode?: string
  search?: string
  page?: number
  pageSize?: number
  locale?: string
}
